// @ts-nocheck
import { useState, useEffect, useMemo } from 'react'

import { Outlet } from 'react-router-dom'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

// Layout Imports
import LayoutWrapper from '@layouts/LayoutWrapper'
import VerticalLayout from '@layouts/VerticalLayout'
import HorizontalLayout from '@layouts/HorizontalLayout'

// Component Imports
import Navigation from '@components/layout/vertical/Navigation'
import Navbar from '@components/layout/vertical/Navbar'
import VerticalFooter from '@components/layout/vertical/Footer'
import Header from '@components/layout/horizontal/Header'
import HorizontalFooter from '@components/layout/horizontal/Footer'
import Customizer from '@core/components/customizer'
import ScrollToTop from '@core/components/scroll-to-top'

// Provider Imports
import { VerticalNavProvider } from '@menu/contexts/verticalNavContext'
import { SettingsProvider } from '@core/contexts/settingsContext'
import ThemeProvider from '@components/theme'
import ReduxProvider from '@/redux-store/ReduxProvider'
import AppReactToastify from '@/libs/styles/AppReactToastify'

// Config
import { i18n } from '@configs/i18n'

// Utils
import { getMode, getSettingsFromCookie, getSystemMode } from '@/utils/clientHelpers'
import { withCountryCurrency } from '@/utils/currency'

// Client-side dictionary loader (replaces server-only getDictionary)
const dictionaries = {
  en: () => import('@/data/dictionaries/en.json').then(m => withCountryCurrency(m.default, 'en')),
  ar: () => import('@/data/dictionaries/ar.json').then(m => withCountryCurrency(m.default, 'ar'))
}

const DashboardLayout = () => {
  const [dictionary, setDictionary] = useState<Record<string, any> | null>(null)

  const lang: 'en' | 'ar' = useMemo(() => {
    try {
      const stored = localStorage.getItem('app-locale')

      return stored === 'ar' ? 'ar' : 'en'
    } catch {
      return 'en'
    }
  }, [])

  const direction = i18n.langDirection[lang]

  const mode = useMemo(() => getMode(), [])
  const settingsCookie = useMemo(() => getSettingsFromCookie(), [])
  const systemMode = useMemo(() => getSystemMode(), [])

  useEffect(() => {
    // Sync html attrs in case page wasn't reloaded
    document.documentElement.lang = lang
    document.documentElement.dir = direction
    dictionaries[lang]().then(setDictionary)
  }, [lang, direction])

  if (!dictionary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <VerticalNavProvider>
      <SettingsProvider settingsCookie={settingsCookie} mode={mode}>
        <ThemeProvider direction={direction} systemMode={systemMode}>
          <ReduxProvider>
            <LayoutWrapper
              systemMode={systemMode}
              verticalLayout={
                <VerticalLayout
                  navigation={<Navigation dictionary={dictionary} mode={mode} />}
                  navbar={<Navbar />}
                  footer={<VerticalFooter />}
                >
                  <Outlet />
                </VerticalLayout>
              }
              horizontalLayout={
                <HorizontalLayout header={<Header dictionary={dictionary || {}} />} footer={<HorizontalFooter />}>
                  <Outlet />
                </HorizontalLayout>
              }
            />
            <ScrollToTop className='mui-fixed'>
              <Button
                variant='contained'
                className='is-10 bs-10 rounded-full p-0 min-is-0 flex items-center justify-center'
              >
                <i className='tabler-arrow-up' />
              </Button>
            </ScrollToTop>
            <Customizer dir={direction} />
          </ReduxProvider>
          <AppReactToastify direction={direction} hideProgressBar />
        </ThemeProvider>
      </SettingsProvider>
    </VerticalNavProvider>
  )
}

export default DashboardLayout
